import DonutChart, {
  DonutChartDatum,
} from "@/components/molecules/charts/DonutChart";
import { formatEuro } from "@/helpers/CurrencyHelpers";
import { FC } from "react";
import { Card, Col, Row, Spinner, Table } from "react-bootstrap";

export interface BreakdownRow {
  key: string;
  label: string;
  color: string;
  amount?: number;
  count: number;
}

interface Props {
  title: string;
  description?: string;
  rows: BreakdownRow[];
  metric: "amount" | "count";
  grandAmount?: number;
  grandCount: number;
  isLoading?: boolean;
}

const BreakdownCard: FC<Props> = ({
  title,
  description,
  rows,
  metric,
  grandAmount,
  grandCount,
  isLoading = false,
}) => {
  const donutData: DonutChartDatum[] = rows.map((row) => ({
    key: row.key,
    label: row.label,
    value: metric === "amount" ? (row.amount ?? 0) : row.count,
    color: row.color,
  }));

  const sortedRows = [...rows].sort((a, b) => {
    const aValue = metric === "amount" ? (a.amount ?? 0) : a.count;
    const bValue = metric === "amount" ? (b.amount ?? 0) : b.count;
    return bValue - aValue;
  });

  return (
    <Card className="h-100 shadow-none border">
      <Card.Body>
        <div className="mb-3">
          <h6 className="mb-0">{title}</h6>
          {description && (
            <p className="text-muted fs--1 mb-0">{description}</p>
          )}
        </div>

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner />
          </div>
        ) : (
          <>
            <Row className="g-2 mb-3">
              {metric === "amount" && (
                <Col xs={6}>
                  <div className="text-muted fs--1">Total</div>
                  <div className="fs-3 fw-bold">
                    {formatEuro(grandAmount ?? 0)}
                  </div>
                </Col>
              )}
              <Col xs={metric === "amount" ? 6 : 12}>
                <div className="text-muted fs--1">Nombre</div>
                <div className="fs-3 fw-bold">{grandCount}</div>
              </Col>
            </Row>

            <DonutChart
              data={donutData}
              valueFormatter={metric === "amount" ? formatEuro : undefined}
            />

            <Table responsive size="sm" className="mb-0 mt-3 fs--1">
              <thead>
                <tr>
                  <th>Libellé</th>
                  {metric === "amount" && <th className="text-end">Montant</th>}
                  <th className="text-end">Nombre</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  <tr key={row.key}>
                    <td>
                      <span
                        className="d-inline-block me-2"
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 2,
                          background: row.color,
                        }}
                      />
                      {row.label}
                    </td>
                    {metric === "amount" && (
                      <td className="text-end">{formatEuro(row.amount ?? 0)}</td>
                    )}
                    <td className="text-end">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default BreakdownCard;
